import Phaser from 'phaser';
import type { NormPoint, PlacementRect } from '../../core/types';
import { ColoringEngine } from '../../core/engine';
import { PALETTE, RESET_HOLD_MS } from '../../core/config';
import { screenToNormalized, type CanvasMapping } from '../../core/coordinates';
import { CanvasArtworkRenderer } from '../../render/CanvasArtworkRenderer';
import { PointerInputAdapter } from '../../input/PointerInputAdapter';
import { PngExporter } from '../../export/PngExporter';
import { IndexedDbStore, MemoryStore } from '../../persistence/IndexedDbStore';
import type { ArtworkStore } from '../../persistence/ArtworkStore';
import { AutoSave, type SaveStatus } from '../../persistence/AutoSave';
import { createEmptyDocument } from '../../persistence/schema';
import { DebugOverlay, isDebugEnabled } from '../../debug/DebugOverlay';
import { HoldTimer } from '../HoldTimer';
import {
  ARTWORK_PIXELS,
  CONTROL_BAR_HEIGHT,
  GAME_HEIGHT,
  GAME_WIDTH,
  computeArtworkRect,
} from '../layout';

/**
 * ColoringScene: the single P0A screen. Phaser owns the scene lifecycle,
 * scaling, the control-bar UI and simple visual feedback. The drawing itself is
 * handled by the framework-free ColoringEngine + a Canvas 2D renderer, shown in
 * Phaser as a live canvas texture.
 */
export class ColoringScene extends Phaser.Scene {
  private engine!: ColoringEngine;
  private artRenderer!: CanvasArtworkRenderer;
  private exporter!: PngExporter;
  private input2!: PointerInputAdapter;
  private artworkTexture: Phaser.Textures.CanvasTexture | null = null;
  private artworkRect!: PlacementRect;

  private autoSave: AutoSave | null = null;
  private saveStatus: SaveStatus = 'idle';
  private lastPointer: { clientX: number; clientY: number; norm: NormPoint } | null = null;

  private selectionRing!: Phaser.GameObjects.Arc;
  private eraserHighlight!: Phaser.GameObjects.Arc;

  // Reset press-and-hold state.
  private holdTimer = new HoldTimer(RESET_HOLD_MS);
  private holdStart: { x: number; y: number } | null = null;
  private holdArc!: Phaser.GameObjects.Graphics;
  private resetCenter = { x: 0, y: 0 };
  private resetRadius = 54;
  // The hold is driven by an independent interval (not Phaser's update loop,
  // which pauses when the window loses focus) so it always advances reliably.
  private holdInterval: ReturnType<typeof setInterval> | null = null;
  private holdLast = 0;

  constructor() {
    super({ key: 'Coloring' });
  }

  create(): void {
    this.artworkRect = computeArtworkRect();

    // Background fills the whole logical screen (extends into letterbox area
    // via the camera background too).
    this.cameras.main.setBackgroundColor('#0b3a5b');
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x0b3a5b).setOrigin(0, 0);
    this.add
      .rectangle(0, GAME_HEIGHT - CONTROL_BAR_HEIGHT, GAME_WIDTH, CONTROL_BAR_HEIGHT, 0x082a42)
      .setOrigin(0, 0);

    // --- artwork surface (custom Canvas 2D renderer shown as a Phaser texture)
    const artworkCanvas = document.createElement('canvas');
    artworkCanvas.width = ARTWORK_PIXELS;
    artworkCanvas.height = ARTWORK_PIXELS;
    this.artRenderer = new CanvasArtworkRenderer(artworkCanvas);
    this.exporter = new PngExporter(this.artRenderer);

    this.artworkTexture = this.textures.addCanvas('artwork', artworkCanvas);
    this.artRenderer.setAfterRender(() => this.artworkTexture?.refresh());

    this.add
      .image(this.artworkRect.x, this.artworkRect.y, 'artwork')
      .setOrigin(0, 0)
      .setDisplaySize(this.artworkRect.width, this.artworkRect.height);

    // --- engine
    this.engine = new ColoringEngine({ renderer: this.artRenderer });
    this.engine.onChange(() => {
      /* renderer already updated by engine; texture refresh happens in afterRender */
    });

    // --- raw pointer drawing on the game canvas
    this.input2 = new PointerInputAdapter({
      target: this.game.canvas,
      engine: this.engine,
      toNormalized: (clientX, clientY) => this.toNormalized(clientX, clientY),
      onPointer: (info) => {
        this.lastPointer = info;
      },
    });
    this.input2.attach();

    this.buildControlBar();
    this.initPersistence();
    this.maybeStartDebug();

    // Clean up native listeners when the scene shuts down.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  // --- coordinate mapping ----------------------------------------------------

  private toNormalized(clientX: number, clientY: number): NormPoint {
    const rect = this.game.canvas.getBoundingClientRect();
    const mapping: CanvasMapping = {
      bounds: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      gameWidth: GAME_WIDTH,
      gameHeight: GAME_HEIGHT,
    };
    return screenToNormalized({ x: clientX, y: clientY }, mapping, this.artworkRect);
  }

  // --- control bar UI --------------------------------------------------------

  private buildControlBar(): void {
    const barY = GAME_HEIGHT - CONTROL_BAR_HEIGHT / 2;
    const r = 54;

    // Selection ring (drawn behind color buttons).
    this.selectionRing = this.add
      .circle(0, barY, r + 10)
      .setStrokeStyle(8, 0xffffff)
      .setVisible(true);

    const colors: Array<{ key: string; hex: string; x: number }> = [
      { key: 'red', hex: PALETTE.red, x: 96 },
      { key: 'yellow', hex: PALETTE.yellow, x: 224 },
      { key: 'blue', hex: PALETTE.blue, x: 352 },
      { key: 'turquoise', hex: PALETTE.turquoise, x: 480 },
    ];
    for (const c of colors) {
      const num = Phaser.Display.Color.HexStringToColor(c.hex).color;
      const btn = this.add
        .circle(c.x, barY, r, num)
        .setStrokeStyle(4, 0x082a42)
        .setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => this.selectColor(c.hex, c.x, barY));
    }

    // Eraser (primary tool group, still separated from destructive reset).
    const eraserX = 624;
    this.eraserHighlight = this.add
      .circle(eraserX, barY, r + 10)
      .setStrokeStyle(8, 0xffffff)
      .setVisible(false);
    const eraser = this.add
      .circle(eraserX, barY, r, 0xf3c6dd)
      .setStrokeStyle(4, 0x082a42)
      .setInteractive({ useHandCursor: true });
    this.add.rectangle(eraserX, barY, 46, 30, 0xffffff).setStrokeStyle(3, 0x6a3550);
    eraser.on('pointerdown', () => this.selectEraser(eraserX, barY));

    // Undo (non-destructive; near primary controls).
    const undoX = 760;
    const undo = this.add
      .circle(undoX, barY, r, 0x9fb3c8)
      .setStrokeStyle(4, 0x082a42)
      .setInteractive({ useHandCursor: true });
    this.drawUndoIcon(undoX, barY);
    undo.on('pointerdown', () => this.engine.undo());

    // Save (right group).
    const saveX = GAME_WIDTH - 232;
    const save = this.add
      .circle(saveX, barY, r, 0x5bbf82)
      .setStrokeStyle(4, 0x082a42)
      .setInteractive({ useHandCursor: true });
    this.drawSaveIcon(saveX, barY);
    save.on('pointerdown', () => void this.exporter.download());

    // Reset (DESTRUCTIVE — far corner, isolated, press-and-hold only).
    const resetX = GAME_WIDTH - 96;
    this.resetCenter = { x: resetX, y: barY };
    this.resetRadius = r;
    const reset = this.add
      .circle(resetX, barY, r, 0xd0664f)
      .setStrokeStyle(4, 0x082a42)
      .setInteractive({ useHandCursor: true });
    this.drawResetIcon(resetX, barY);
    this.holdArc = this.add.graphics();
    reset.on('pointerdown', (p: Phaser.Input.Pointer) => this.beginResetHold(p));

    this.input.on('pointerup', () => this.cancelResetHold());
    this.input.on('pointerupoutside', () => this.cancelResetHold());
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => this.trackResetHold(p));

    // Initialise selection to the default color (red).
    this.selectColor(PALETTE.red, colors[0].x, barY);
  }

  private selectColor(hex: string, x: number, y: number): void {
    this.engine.setTool('marker');
    this.engine.setColor(hex);
    this.selectionRing.setPosition(x, y).setVisible(true);
    this.eraserHighlight.setVisible(false);
  }

  private selectEraser(x: number, y: number): void {
    this.engine.setTool('eraser');
    this.eraserHighlight.setPosition(x, y).setVisible(true);
    this.selectionRing.setVisible(false);
  }

  // --- reset press-and-hold --------------------------------------------------

  private beginResetHold(p: Phaser.Input.Pointer): void {
    if (this.holdInterval !== null) return;
    this.holdTimer.start();
    this.holdStart = { x: p.x, y: p.y };
    this.holdLast = performance.now();
    this.holdInterval = setInterval(() => this.tickResetHold(), 16);
  }

  private trackResetHold(p: Phaser.Input.Pointer): void {
    if (!this.holdTimer.isActive() || !this.holdStart) return;
    const dx = p.x - this.holdStart.x;
    const dy = p.y - this.holdStart.y;
    const moved = Math.hypot(dx, dy);
    // Cancel if the finger wanders away from the button.
    if (moved > this.resetRadius * 1.1) this.cancelResetHold();
  }

  private cancelResetHold(): void {
    if (this.holdInterval !== null) {
      clearInterval(this.holdInterval);
      this.holdInterval = null;
    }
    this.holdTimer.cancel();
    this.holdStart = null;
    this.holdArc.clear();
  }

  private tickResetHold(): void {
    if (!this.holdTimer.isActive()) return;
    const now = performance.now();
    const delta = now - this.holdLast;
    this.holdLast = now;
    const { progress, completed } = this.holdTimer.update(delta);
    this.holdArc.clear();
    this.holdArc.lineStyle(10, 0xffffff, 0.95);
    this.holdArc.beginPath();
    this.holdArc.arc(
      this.resetCenter.x,
      this.resetCenter.y,
      this.resetRadius + 14,
      -Math.PI / 2,
      -Math.PI / 2 + progress * Math.PI * 2,
      false,
    );
    this.holdArc.strokePath();
    if (completed) {
      this.cancelResetHold();
      this.engine.reset();
    }
  }

  // --- simple drawn icons ----------------------------------------------------

  private drawUndoIcon(x: number, y: number): void {
    const g = this.add.graphics();
    g.lineStyle(8, 0x27384a, 1);
    g.beginPath();
    g.arc(x, y, 22, Math.PI * 0.4, Math.PI * 1.9, false);
    g.strokePath();
    g.fillStyle(0x27384a, 1);
    g.fillTriangle(x - 26, y - 6, x - 26, y - 30, x - 6, y - 18);
  }

  private drawSaveIcon(x: number, y: number): void {
    const g = this.add.graphics();
    g.lineStyle(8, 0x0e3a24, 1);
    g.beginPath();
    g.moveTo(x, y - 24);
    g.lineTo(x, y + 10);
    g.strokePath();
    g.fillStyle(0x0e3a24, 1);
    g.fillTriangle(x - 16, y + 2, x + 16, y + 2, x, y + 22);
    g.lineBetween(x - 24, y + 28, x + 24, y + 28);
  }

  private drawResetIcon(x: number, y: number): void {
    const g = this.add.graphics();
    g.lineStyle(8, 0x5a1f13, 1);
    g.beginPath();
    g.arc(x, y, 20, Math.PI * 1.1, Math.PI * 0.5, false);
    g.strokePath();
    g.fillStyle(0x5a1f13, 1);
    g.fillTriangle(x + 6, y - 26, x + 26, y - 20, x + 10, y - 6);
  }

  // --- persistence -----------------------------------------------------------

  private initPersistence(): void {
    void (async () => {
      let store: ArtworkStore = new IndexedDbStore();
      let loaded = null;
      try {
        loaded = await store.load();
      } catch {
        store = new MemoryStore(); // fall back if IDB misbehaves
      }
      const doc = loaded ?? createEmptyDocument();
      this.engine.loadDocument(doc);

      this.autoSave = new AutoSave(this.engine, store);
      this.autoSave.onStatus((s) => (this.saveStatus = s));
      this.autoSave.start();
    })();
  }

  // --- debug -----------------------------------------------------------------

  private maybeStartDebug(): void {
    if (!isDebugEnabled()) return;
    new DebugOverlay({
      engine: this.engine,
      getPointer: () => this.lastPointer,
      getSaveStatus: () => this.saveStatus,
      getSwStatus: () => (this.registry.get('swStatus') as string) ?? 'unknown',
      getViewport: () => ({ width: window.innerWidth, height: window.innerHeight }),
    });
  }

  private teardown(): void {
    this.cancelResetHold();
    this.input2?.detach();
    this.autoSave?.dispose();
  }
}
